'use client';

import { useEffect, useRef } from 'react';
import { usePageTracePolicy } from '@/app/contexts/TracePolicyContext';
import { useInteractionArea } from '@/app/contexts/InteractionAreaContext';
import { addLogToBuffer } from '@/app/utils/loggingInterval';
import { EVENT_NAME_PREFIX } from './constants';
import type { InteractionEvent, AnalyticsTargetArea } from '@/app/types/analytics';

/**
 * Minimum time in milliseconds that an element must be 50% visible
 * before a VPV impression is logged.
 */
const VPV_VISIBILITY_DELAY_MS = 250;

interface UseImpressionOptions {
  /**
   * The intersection threshold to trigger the impression.
   * - "1px": Triggers immediately when 1px of the element is visible (uses `one_px_impression`)
   * - "50%": Triggers when 50% of the element is visible for at least 250ms (uses `vpv_impression`)
   * @default "1px"
   */
  threshold?: '1px' | '50%';

  /**
   * Whether the impression should only be logged once per mount.
   * When false, logs an impression each time the element enters the viewport.
   * @default true
   */
  once?: boolean;

  /**
   * Custom action target name. Defaults to "unknown".
   */
  actionTarget?: string;

  /**
   * Override target area from InteractionAreaContext.
   */
  targetArea?: AnalyticsTargetArea;

  /**
   * Additional metadata to include with the event.
   */
  metadata?: Record<string, unknown>;

  /**
   * Whether the hook is enabled. Set to false to disable tracking.
   * @default true
   */
  enabled?: boolean;

  /**
   * Root margin for the intersection observer.
   * @see IntersectionObserver.rootMargin
   */
  rootMargin?: string;
}

/**
 * Hook that tracks when an element enters the viewport (impressions).
 *
 * Uses the Intersection Observer API to detect when a referenced element
 * becomes visible in the viewport and logs an impression event.
 *
 * Note: The "midjen." prefix is automatically added to eventName if not present.
 *
 * Impression types:
 * - `one_px_impression`: Logs immediately when 1px of the element is visible
 * - `vpv_impression`: Logs when the element is at least 50% visible for at least 250ms
 *
 * @param eventName - The event name to log (should end with "_impression")
 * @param options - Configuration options for the impression tracking
 * @returns A ref callback to attach to the element you want to track
 *
 * @example
 * ```tsx
 * // Basic usage - track when component enters viewport (1px)
 * function MyCard() {
 *   const impressionRef = useImpression("card_impression");
 *
 *   return (
 *     <div ref={impressionRef}>
 *       Card content...
 *     </div>
 *   );
 * }
 *
 * // With 50% visibility threshold (250ms delay)
 * function MyMedia() {
 *   const impressionRef = useImpression("media_impression", {
 *     threshold: "50%",
 *     actionTarget: "MediaCard",
 *   });
 *
 *   return <img ref={impressionRef} src="..." />;
 * }
 *
 * // Track multiple impressions (e.g., for gallery items)
 * function GalleryItem({ id }: { id: string }) {
 *   const impressionRef = useImpression("gallery_item_impression", {
 *     once: false,
 *     metadata: { itemId: id },
 *   });
 *
 *   return <div ref={impressionRef}>...</div>;
 * }
 * ```
 */
export function useImpression(
  eventName: string,
  options: UseImpressionOptions = {}
): (element: HTMLElement | null) => void {
  const {
    threshold = '1px',
    once = true,
    actionTarget = 'unknown',
    targetArea: targetAreaOverride,
    metadata,
    enabled = true,
    rootMargin,
  } = options;

  const tracePolicy = usePageTracePolicy();
  const { targetArea: contextTargetArea } = useInteractionArea();
  const targetArea = targetAreaOverride ?? contextTargetArea;

  const hasLoggedRef = useRef(false);
  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const vpvTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear VPV timer
  const clearVpvTimer = () => {
    if (vpvTimerRef.current !== null) {
      clearTimeout(vpvTimerRef.current);
      vpvTimerRef.current = null;
    }
  };

  // Log impression event
  const logImpression = () => {
    if (!enabled) return;
    if (once && hasLoggedRef.current) return;

    hasLoggedRef.current = true;

    // Add prefix if not already present
    const prefixedEventName = eventName.startsWith(EVENT_NAME_PREFIX)
      ? eventName
      : `${EVENT_NAME_PREFIX}${eventName}`;

    const event: InteractionEvent = {
      actionTarget,
      actionType: threshold === '50%' ? 'vpv_impression' : 'one_px_impression',
      eventName: prefixedEventName,
      httpReferrer: typeof window !== 'undefined' ? window.location.href : '',
      interface: 'desktop_web',
      targetArea: targetArea ?? undefined,
      timestamp: Date.now(),
      tracePolicyReferrer: tracePolicy ?? '',
      ...(metadata && { metadata }),
    };

    addLogToBuffer(event);
    if (process.env.NODE_ENV === 'development') {
      console.debug('[useImpression] Impression logged', {
        eventName: prefixedEventName,
        threshold,
        targetArea,
        metadata,
      });
    }
  };

  // Cleanup observer
  const cleanupObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    clearVpvTimer();
  };

  // Create observer
  const createObserver = () => {
    if (!enabled || typeof window === 'undefined') return null;

    const observerThreshold = threshold === '50%' ? 0.5 : 0;
    const observerOptions: IntersectionObserverInit = {
      threshold: observerThreshold,
      rootMargin,
    };

    return new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (threshold === '50%') {
            // VPV: Start timer for 250ms visibility requirement
            if (vpvTimerRef.current === null) {
              if (process.env.NODE_ENV === 'development') {
                console.debug('[useImpression] VPV timer started', {
                  eventName,
                  delay: VPV_VISIBILITY_DELAY_MS,
                });
              }
              vpvTimerRef.current = setTimeout(() => {
                logImpression();
                vpvTimerRef.current = null;

                // If once mode, stop observing after first impression
                if (once && observerRef.current && elementRef.current) {
                  observerRef.current.unobserve(elementRef.current);
                }
              }, VPV_VISIBILITY_DELAY_MS);
            }
          } else {
            // 1px: Log immediately
            logImpression();

            // If once mode, stop observing after first impression
            if (once && observerRef.current && elementRef.current) {
              observerRef.current.unobserve(elementRef.current);
            }
          }
        } else {
          // Element left viewport - clear VPV timer if active
          if (threshold === '50%' && vpvTimerRef.current !== null) {
            if (process.env.NODE_ENV === 'development') {
              console.debug('[useImpression] VPV timer cancelled - element left viewport', {
                eventName,
              });
            }
            clearVpvTimer();
          }
        }
      }
    }, observerOptions);
  };

  // Reset hasLogged when eventName changes
  useEffect(() => {
    hasLoggedRef.current = false;
  }, [eventName]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupObserver();
    };
  }, [cleanupObserver]);

  // Ref callback that sets up the observer when element is attached
  const setRef = (element: HTMLElement | null) => {
    // Cleanup previous observer
    cleanupObserver();
    elementRef.current = element;

    if (!element || !enabled) return;

    // Create and attach observer
    observerRef.current = createObserver();
    if (observerRef.current) {
      observerRef.current.observe(element);
    }
  };

  return setRef;
}
